/**
 * <sticky-add-to-cart> — bottom sticky add-to-cart bar (see snippets/sticky-atc.liquid).
 *
 * - Visibility: shown once the main Add-to-cart button has scrolled above the
 *   viewport. After the shopper picks a variant (the gallery then scrolls the page
 *   up to the new image, leaving the main button below the fold) it also shows
 *   while the main button is below the viewport.
 * - Two-way variant sync: the bar has one dropdown per product option. Changing one
 *   updates the whole product page (price / image / swatches) via the main variant
 *   picker, and changing the variant on the page updates the bar. The bar keeps its
 *   own <product-form> and hidden variant id (resolved from its JSON variant table),
 *   so adding to cart never depends on the page's async variant fetch.
 */
class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.variantPicked = false;
    this.onVariantChange = this.onVariantChange.bind(this);
  }

  connectedCallback() {
    // position: fixed must anchor to the viewport, but the bar is rendered inside
    // <product-info>, whose ancestors carry transforms (which trap fixed children).
    // Re-parent to <body> once; the move re-enters connectedCallback.
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
      return;
    }

    this.optionSelects = Array.from(this.querySelectorAll('[data-sticky-option]'));
    this.idInput = this.querySelector('[data-sticky-id]');
    try {
      this.variants = JSON.parse(this.querySelector('[data-sticky-variants]')?.textContent || '[]');
    } catch (e) {
      this.variants = [];
    }
    this.submit = this.querySelector('[data-sticky-submit]');
    this.submitText = this.querySelector('[data-sticky-submit-text]');
    this.priceEl = this.querySelector('[data-sticky-price]');

    this.optionSelects.forEach((select) => select.addEventListener('change', () => this.onSelectChange()));

    // Reflect page-side variant changes back into the bar.
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
      }
    } catch (e) {
      /* pub/sub unavailable — page->bar sync is a progressive enhancement */
    }

    this.setupVisibility();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      window.removeEventListener('resize', this.scrollHandler);
    }
    this.observer?.disconnect();
  }

  // ---- visibility ----
  // Scroll-based (not IntersectionObserver): IO only fires at the crossing edge,
  // so a one-shot `boundingClientRect.top < 0` check misses once you keep
  // scrolling. A rAF-throttled scroll check evaluates continuously and reliably.
  setupVisibility() {
    this.scrollHandler = () => {
      const target = this.dataset.observe ? document.querySelector(this.dataset.observe) : null;
      if (!target) {
        this.toggle(window.scrollY > 500);
        return;
      }
      const rect = target.getBoundingClientRect();
      const above = rect.bottom < 0;
      const below = rect.top > window.innerHeight;
      // Before any variant pick: only once the main button is above the viewport,
      // so the bar does not appear on page load. After a pick: whenever it is off-screen.
      this.toggle(above || (this.variantPicked && below));
    };
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    window.addEventListener('resize', this.scrollHandler, { passive: true });

    // Backup trigger: re-evaluate whenever the main button crosses the viewport,
    // in case scroll events are throttled or the page scrolls a container.
    const target = this.dataset.observe ? document.querySelector(this.dataset.observe) : null;
    if (target && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(() => this.scrollHandler(), { threshold: [0, 1] });
      this.observer.observe(target);
    }

    this.scrollHandler();
  }

  toggle(show) {
    // Always force a boolean: classList.toggle(name, undefined) flips the class.
    show = Boolean(show);
    this.classList.toggle('is-visible', show);
    // Lift the floating back-to-top button above the bar (it sits bottom-center).
    document.body.classList.toggle('sticky-atc-visible', show);
  }

  // ---- bar dropdowns -> page ----
  onSelectChange() {
    const values = this.optionSelects.map((select) => select.value);
    const variant = this.variants.find((v) => v.options.every((value, index) => value === values[index]));
    // No such combination: keep the last valid id but block adding.
    if (variant && this.idInput) this.idInput.value = variant.id;
    this.updatePrice(variant?.price);
    this.setAvailability(Boolean(variant?.available));
    this.drivePageVariant(values);
  }

  // values: the option values in option order, e.g. ["Forest Green", "S"].
  drivePageVariant(values) {
    const main = document.getElementById(`variant-selects-${this.sectionId}`);
    if (!main) return;
    let dispatched = false;
    values.forEach((val) => {
      // Radios (swatch / button pickers).
      const radio = Array.from(main.querySelectorAll('input[type="radio"]')).find((i) => i.value === val);
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        dispatched = true;
        return;
      }
      // Dropdown pickers.
      const select = Array.from(main.querySelectorAll('select')).find((s) =>
        Array.from(s.options).some((o) => o.value === val)
      );
      if (select && select.value !== val) {
        select.value = val;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        dispatched = true;
      }
    });
    return dispatched;
  }

  // ---- page -> bar ----
  onVariantChange(event) {
    this.variantPicked = true;
    // The gallery scrolls to the new variant image after this event; the scroll
    // handler re-evaluates as it moves, this covers the case where it does not.
    this.scrollHandler?.();
    const payload = event?.data ?? event;
    const variant = payload?.variant;
    if (!variant) {
      // Unavailable option combination (or a single-variant product gone unavailable).
      this.setAvailability(false);
      return;
    }
    if (this.idInput) this.idInput.value = variant.id;
    this.optionSelects.forEach((select, index) => {
      const value = variant.options?.[index];
      if (value !== undefined && select.value !== value) select.value = value;
    });
    this.updatePrice(this.variants.find((v) => v.id === variant.id)?.price);
    this.setAvailability(variant.available);
  }

  // ---- UI helpers ----
  updatePrice(priceText) {
    if (priceText && this.priceEl) this.priceEl.textContent = priceText;
  }

  setAvailability(available) {
    if (!this.submit) return;
    if (available) {
      this.submit.removeAttribute('aria-disabled');
      if (this.submitText) this.submitText.textContent = window.variantStrings?.addToCart || 'Add to cart';
    } else {
      this.submit.setAttribute('aria-disabled', 'true');
      if (this.submitText) this.submitText.textContent = window.variantStrings?.soldOut || 'Sold out';
    }
  }
}

customElements.define('sticky-add-to-cart', StickyAddToCart);
